import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  Container,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import React, { useState } from "react";
import { DropzoneAreaBase } from "react-mui-dropzone";
import { CustomButton, CustomInput } from "../../../global/components";
import CustomLoader from "../../../global/components/CustomLoader/CustomLoader";
import notifiers from "../../../global/constants/NotificationConstants";
import strings from "../../../global/constants/StringConstants";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../helpers/methods";
import { selectUserName } from "../../../redux/authSlice";
import { useAppSelector } from "../../../utils/hooks";
import * as addBlogService from "./AddBlog.service";
import addBlogStyle from "./AddBlog.styles";
import { addBlogForm, generateHeadlineValidation } from "./AddBlogValidation";

// 1. Add to initial form state
type BlogFormType = typeof addBlogForm & {
  website?: { value: string; error?: string };
  metaTitle?: { value: string; error?: string };
  metaDescription?: { value: string; error?: string };
};

const defaultAddBlogForm = {
  ...addBlogForm,
  website: { value: "" },
  metaTitle: { value: "" },
  metaDescription: { value: "" },
};

const AddBlog = () => {
  const classes = addBlogStyle;
  const [blogForm, setBlogForm] = useState<any>(defaultAddBlogForm);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>("");
  const [isCustomTopic, setIsCustomTopic] = useState<boolean>(false);
  const [customTopicInput, setCustomTopicInput] = useState<string>("");
  const [customTopics, setCustomTopics] = useState<Array<{ name: string }>>([]);
  const userName = useAppSelector(selectUserName);

  const baseTopics = [
    { name: "Hydroponics" },
    { name: "Technology" },
    { name: "Innovation" },
    { name: "Wellness" },
    { name: "Philanthropy" },
    { name: "Entrepreneurship" },
    { name: "Social Entrepreneurship" },
    { name: "Other", value: "CUSTOM_TOPIC" },
  ];

  const topics = [...baseTopics, ...customTopics];

  const handleChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;

    if (selectedValue === "CUSTOM_TOPIC") {
      setIsCustomTopic(true);
      setCustomTopicInput("");
    } else {
      setIsCustomTopic(false);
      setBlogForm((prev: any) => ({
        ...prev,
        key: {
          ...prev.key,
          value: selectedValue,
        },
      }));
    }
  };

  const handleCustomTopicChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const customValue = event.target.value;
    setCustomTopicInput(customValue);
  };

  const handleCustomTopicKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && customTopicInput.trim()) {
      event.preventDefault();
      addCustomTopic();
    }
  };

  const handleCustomTopicBlur = () => {
    if (customTopicInput.trim()) {
      addCustomTopic();
    } else {
      setIsCustomTopic(false);
    }
  };

  const addCustomTopic = () => {
    const newCustomTopic = customTopicInput.trim();

    if (
      newCustomTopic &&
      !topics.some((topic) => topic.name === newCustomTopic)
    ) {
      setCustomTopics((prev) => [...prev, { name: newCustomTopic }]);
    }

    setBlogForm((prev: any) => ({
      ...prev,
      key: {
        ...prev.key,
        value: newCustomTopic,
      },
    }));

    setIsCustomTopic(false);
  };

  const handleTextFields = (event: React.ChangeEvent<any>) => {
    setBlogForm((prev: any) => ({
      ...prev,
      [event.target.name]: {
        ...prev[event.target.name],
        value: event.target.value,
      },
    }));
  };

  const generateContent = async () => {
    try {
      if (!isTruthy(prompt)) {
        openErrorNotification(
          "Propmt must not be empry. Enter topic or set of key words."
        );
        return;
      }
      setLoader(true);

      const response = await addBlogService.generateBlogContent(prompt);
      setBlogForm((prev: any) => ({
        ...prev,
        content: {
          ...prev.content,
          value: response.result,
        },
      }));
      setLoader(false);
    } catch (error: any) {
      setLoader(false);
      openErrorNotification(
        isTruthy(error.errorMessage)
          ? error.errorMessage
          : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleOnDropZoneFile = async (event: React.ChangeEvent<any> | any) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event[0].file);
      const response = await addBlogService.uploadImage(formData);
      setBlogForm((prev: any) => ({
        ...prev,
        image: {
          ...prev.image,
          value: response.image,
          path: response.imagePath,
        },
      }));
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.errorMessage)
          ? error.errorMessage
          : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleSummary = async () => {
    try {
      if (handleValidation(false)) {
        setIsLoading(true);
        const response = await addBlogService.generateSummary(
          blogForm.content.value
        );
        setBlogForm((prev: any) => ({
          ...prev,
          headline: { value: response.headline, error: "" },
          summary: { value: response.summary, error: "" },
        }));
        setIsVisible(true);
        setIsRequired(true);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.errorMessage)
          ? error.errorMessage
          : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleSubmit = async () => {
    try {
      if (handleValidation(true)) {
        setIsLoading(true);
        const response = await addBlogService.addBlog(blogForm, userName);
        openSuccessNotification(response.message);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.errorMessage)
          ? error.errorMessage
          : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleValidation = (isSubmit: boolean) => {
    const { isValid, errors } = generateHeadlineValidation(blogForm, isSubmit);
    setBlogForm((prev: any) => ({ ...prev, ...errors }));
    return isValid;
  };

  const handleBlogContent = (content: string) => {
    setBlogForm((prev: any) => ({
      ...prev,
      content: {
        ...prev.content,
        value: content,
      },
    }));
  };

  const getHeader = () => {
    return (
      <Grid container justifyContent={"center"}>
        <Box pt={5} borderBottom={"double"}>
          <Typography sx={classes.header}>Write Your Blog</Typography>
        </Box>
      </Grid>
    );
  };

  const getBlogContentTextfield = () => {
    return (
      <>
        <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
          <Box mb={1}>
            <InputLabel required={isRequired} sx={classes.nameField}>
              Topic Name
            </InputLabel>
          </Box>
          {isCustomTopic ? (
            <CustomInput
              autoFocus
              placeHolder="Enter other topic"
              value={customTopicInput}
              onChange={handleCustomTopicChange}
              onKeyPress={handleCustomTopicKeyPress}
              onBlur={handleCustomTopicBlur}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      Press Enter to confirm
                    </Typography>
                  </InputAdornment>
                ),
              }}
              error={!isTruthy(blogForm.title?.value) && blogForm.title?.error}
            />
          ) : (
            <Select
              displayEmpty
              sx={classes.selectKeys}
              value={blogForm?.key?.value || ""}
              onChange={handleChange}
              error={!!blogForm?.key?.error}
            >
              <MenuItem value="" disabled>
                <Typography>Select Topic Name</Typography>
              </MenuItem>
              {topics.map((topic: any) => (
                <MenuItem key={topic.name} value={topic.value || topic.name}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {topic.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          )}
          {!isTruthy(blogForm?.key?.value) && blogForm?.key?.error && (
            <FormHelperText error sx={classes.errorStyling}>
              {blogForm?.key?.error}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xl={8} lg={8} md={8} sm={12} xs={12}>
          <CustomInput
            placeHolder="Blog Title"
            label={"Blog Title"}
            name="title"
            value={blogForm?.title?.value}
            onChange={handleTextFields}
          />
        </Grid>
        <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
          <CustomInput
            label={"Source"}
            placeHolder="Blog Source"
            name="source"
            value={blogForm?.source?.value}
            onChange={handleTextFields}
          />
        </Grid>
        <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
          <CustomInput
            placeHolder="Blog Author"
            label={"Author"}
            name="author"
            value={blogForm?.author?.value}
            onChange={handleTextFields}
          />
        </Grid>
        <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
          <CustomInput
            placeHolder="Blog Url"
            label={"Url"}
            name="url"
            value={blogForm?.url?.value}
            onChange={handleTextFields}
          />
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <CustomInput
            placeHolder="Website"
            label={"Website"}
            name="website"
            value={blogForm?.website?.value}
            onChange={handleTextFields}
          />
        </Grid>
        {/* <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
          <CustomInput
            placeHolder="Meta Title"
            label={"Meta Title"}
            name="metaTitle"
            value={blogForm?.metaTitle?.value}
            onChange={handleTextFields}
          />
        </Grid> */}
        {/* <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
          <CustomInput
            placeHolder="Meta Description"
            label={"Meta Description"}
            name="metaDescription"
            value={blogForm?.metaDescription?.value}
            onChange={handleTextFields}
          />
        </Grid> */}
        <Grid item xl={9} lg={9} md={9} sm={9} xs={9}>
          <CustomInput
            disabled={loader}
            placeHolder="Enter a topic or set of keywords, and we'll craft a complete blog for you."
            name="prompt"
            value={prompt}
            onChange={(e: React.ChangeEvent<any> | any) => {
              setPrompt(e.target.value);
            }}
          />
        </Grid>

        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <CustomButton
            onClick={generateContent}
            customClasses={{ maxWidth: "350px" }}
            label={"Generate Blog text"}
            disabled={prompt.trim() ? false : true}
            loading={loader}
          />
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <Editor
            value={blogForm?.content?.value}
            apiKey="7qoawbyn9ty4y1jrtaj6cor98lrl0sbp2p90wgtbfa60tso1"
            init={{
              branding: false,
              height: 600,
              menubar: false,
              toolbar:
                "formatselect | bold italic underline | forecolor backcolor blockquote | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | removeformat",
              image_advtab: false,
            }}
            onEditorChange={(content: string) => handleBlogContent(content)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Box sx={classes.customDropZone}>
            <Box mb={1}>
              <InputLabel sx={classes.nameField}>
                Blog Image ( .jpeg / .jpg / .png )
              </InputLabel>
            </Box>
            <DropzoneAreaBase
              fileObjects={[]}
              dropzoneText={"Choose File"}
              onAdd={(event: React.ChangeEvent<any> | any) => {
                handleOnDropZoneFile(event);
              }}
              acceptedFiles={[
                strings.jpeg,
                strings.jpg,
                strings.png,
                strings.gif,
                strings.bmp,
                strings.tiff,
              ]}
              Icon={CloudUploadIcon}
              filesLimit={1}
              maxFileSize={Infinity}
              showAlerts={false}
            />
          </Box>
          <Typography sx={classes.headerTextField}>
            {blogForm?.image?.value}
          </Typography>
        </Grid>
        {!isVisible && (
          <Grid
            item
            xl={12}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={"flex"}
            justifyContent={"center"}
          >
            <CustomButton
              onClick={handleSummary}
              customClasses={{ maxWidth: "350px" }}
              label={"Generate Headline & Summary"}
              disabled={blogForm?.content?.value ? false : true}
            />
          </Grid>
        )}
      </>
    );
  };

  const getSummaryTextfield = () => {
    return (
      <>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <CustomInput
            required
            multiline
            rows={3}
            label={"Blog Headline"}
            name="headline"
            value={blogForm?.headline.value}
            onChange={handleTextFields}
            error={
              !isTruthy(blogForm?.headline.value) && blogForm?.headline.error
            }
          />
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <CustomInput
            required
            multiline
            rows={8}
            label={"Blog Summary"}
            name="summary"
            value={blogForm?.summary.value}
            onChange={handleTextFields}
            error={
              !isTruthy(blogForm?.summary.value) && blogForm?.summary.error
            }
          />
        </Grid>
        <Grid
          item
          xl={12}
          lg={12}
          md={12}
          sm={12}
          xs={12}
          display={"flex"}
          justifyContent={"center"}
        >
          <CustomButton
            onClick={handleSubmit}
            customClasses={{ maxWidth: "350px" }}
            label={"Submit"}
          />
        </Grid>
      </>
    );
  };

  const getAddBlog = () => {
    return (
      <Container maxWidth="lg">
        {getHeader()}
        <Grid container py={4} spacing={4}>
          {getBlogContentTextfield()}
          {isVisible && getSummaryTextfield()}
        </Grid>
        <CustomLoader isLoading={isLoading} />
      </Container>
    );
  };
  return getAddBlog();
};

export default AddBlog;
